import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlContainer,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgStyle } from '@angular/common';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { TruncatePipe } from '../../pipes/truncate/truncate.pipe';
import { LoaderComponent } from '../loader/loader.component';
import { ClickOutSideDirective } from '../../directives/clickOutSide.directive';
import { SearchMinLength } from '../../../core/common/enums/search-min-length';
import { filterInputTypesEnum } from '../../enums/filter-input-type';
import { ApiResponse } from '../../../core/common/models/api-response.model';
import {
  LookupOptionItem,
  LookupFilter,
} from '../lookup-field/lookup-field-item';
import { LookupFieldService } from '../lookup-field/lookup-field.service';
import { AvatarComponent } from '../avatar/avatar.component';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { SearchIconComponent } from '@kuiper/icons';

@Component({
  selector: 'lib-customized-dropdown',
  imports: [
    NgStyle,
    ReactiveFormsModule,
    ClickOutSideDirective,
    TruncatePipe,
    LoaderComponent,
    FormsModule,
    AvatarComponent,
    ChevronDownIconComponent,
    SearchIconComponent,
  ],
  templateUrl: './customized-dropdown.component.html',
  styleUrl: './customized-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomizedDropdownComponent),
      multi: true,
    },
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CustomizedDropdownComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  searchInput = viewChild<ElementRef>('search');

  inputFormControlName = input.required<string>();
  id = input.required<string>();
  name = input.required<string>();

  label = input<string>();
  placeholder = input<string>('');
  inputClass = input<string>('');
  hasSmallGap = input<boolean>(false);
  useFixedDropdown = input<boolean>(false);

  disabled = model<boolean>(false);
  readonly = input<boolean>();
  value = input<string | number>();
  required = input<boolean>(false);

  options = signal<LookupOptionItem[] | null>([]);
  selectedKeyValue = input<'value' | 'id'>('value');
  selectedKeyName = input<'label' | 'name'>('label');
  imageKeyName = input<'image' | 'icon'>('image');

  searchLabel = input<string>('Find Users'); //to be able to translate or edit it
  searchPlaceholder = input<string>('Search by name'); //to be able to translate or edit it
  noSearchResultLabel = input<string>('No search result'); //to be able to translate or edit it
  noDataLabel = input<string>('No data'); //to be able to translate or edit it
  searchFilterKey = input<string>('search');

  changeOption = output<LookupOptionItem>();

  isLoading = signal<boolean>(false);
  apiUrl = input<string>('');
  filters = input<LookupFilter[]>([
    {
      key: this.searchFilterKey(),
      value: '',
    },
  ]);
  page = signal<number>(1);
  pageSize = signal<number>(15);
  totalPages = model<number>(1);

  isDropdownOpen = false;
  selectedOption = model<LookupOptionItem | undefined>();
  focused?: boolean;
  notEmpty?: boolean;
  searchValue!: string;
  showNoSearchResult!: boolean;
  filteredOptions: LookupOptionItem[] = [];
  destroy$ = new Subject<void>();
  apiUrlParams: string[] = [];
  formArray = input<FormArray | undefined>(undefined);
  formArrayIndex = input<number>();
  dynamicArrayFilters = signal<Record<string, number[]>>({});
  dynamicMultipleUrlParams = signal<{ key: string; value: string }[]>([]);

  currentSearchTerm = signal<string>('');
  searchTerm = signal<string>('');
  searchTerm$ = new Subject<string>();
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);

  dropdownStyle: { [key: string]: string } = {};
  private scrollListener: (() => void) | null = null;
  private positioningRef: HTMLElement | null = null;

  readonly elementRef = inject(ElementRef);
  readonly parentContainer = inject(ControlContainer);
  readonly lookupFieldService = inject(LookupFieldService);

  private get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  protected get childControl() {
    return this.parentFormGroup?.controls[
      this.inputFormControlName()
    ] as FormControl;
  }

  private calculateDropdownPosition(): void {
    if (!this.useFixedDropdown()) return;
    const td = this.elementRef.nativeElement.closest('td') as HTMLElement;
    this.positioningRef = td || this.elementRef.nativeElement;
    this.updateDropdownPosition();
    this.addScrollListener();
  }

  private updateDropdownPosition(): void {
    if (!this.positioningRef) return;
    const rect = this.positioningRef.getBoundingClientRect();
    this.dropdownStyle = {
      position: 'fixed',
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      'min-width': 'unset',
      'z-index': '9999',
    };
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    this.scrollListener = () => {
      this.updateDropdownPosition();
    };
    window.addEventListener('scroll', this.scrollListener, true);
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
  }

  ngOnChanges(): void {
    if (!this.childControl?.value) {
      this.selectedOption.set(<LookupOptionItem>{});
    }
  }

  ngOnInit(): void {
    if (this.disabled()) {
      this.childControl.disable();
    }

    if (this.value() && !this.selectedOption()) {
      this.getLookupItems();
    }

    if (this.filters()?.length) {
      // Subscribe to FormArray valueChanges only once if it exists

      if (this.formArray()?.length) {
        // Initialize dynamic array filters with current values
        this.updateDynamicArrayFilters();

        // Subscribe to FormArray valueChanges to update on changes
        this.formArray()
          ?.valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.updateDynamicArrayFilters();
          });
      }

      // Process each filter
      this.filters().forEach((filter, index) => {
        if (filter.value_type === filterInputTypesEnum.DYNAMIC) {
          if (!this.formArray()?.length) {
            const fieldControlName = filter.value.toString();
            this.handleControlDependencies(fieldControlName, index);
          }
        }
      });
    }

    if (
      this.required() &&
      !this.childControl?.value &&
      this.childControl?.touched
    ) {
      this.childControl?.setErrors({ required: true });
    }
  }

  private updateDynamicArrayFilters(): void {
    if (!this.formArray()?.length) {
      this.dynamicArrayFilters.set({});
      this.dynamicMultipleUrlParams.set([]);
      return;
    }

    const newDynamicFilters: Record<string, number[]> = {};
    const filterValues: { key: string; value: string }[] = [];

    // Find all dynamic filters
    const dynamicFilters = this.filters().filter(
      filter => filter.value_type === filterInputTypesEnum.DYNAMIC
    );

    // For each dynamic filter, collect values from form array
    dynamicFilters.forEach(filter => {
      const fieldName = filter.value.toString();
      const values: number[] = [];

      // Collect all values for this field from form array controls
      this.formArray()?.controls.forEach((control, controlIndex) => {
        const formGroup = control as FormGroup;
        const fieldControl = formGroup.get(fieldName);
        if (fieldControl?.value !== null && fieldControl?.value !== undefined) {
          values.push(fieldControl.value);
          // Format for dynamicMultipleUrlParams
          filterValues.push({
            key: `filters[${filter.key}][${controlIndex}]`,
            value: String(fieldControl.value),
          });
        }
      });

      newDynamicFilters[fieldName] = values;
    });

    this.dynamicArrayFilters.set(newDynamicFilters);
    this.dynamicMultipleUrlParams.set(filterValues);
  }

  handleControlDependencies(fieldControlName: string, index: number) {
    const fieldControl = this.parentFormGroup?.controls[fieldControlName];
    if (fieldControl.value) {
      this.apiUrlParams[index] = fieldControl.value;
    }

    fieldControl?.valueChanges?.subscribe(value => {
      if (!value) {
        this.disabled.set(true);
        this.childControl.disable();
      } else if (this.isDependencyFieldsValueAvailable()) {
        this.disabled.set(false);
        this.childControl.enable();
      }
      this.selectedOption.set({} as LookupOptionItem);
      this.childControl.patchValue(null);
      this.apiUrlParams[index] = value;
    });
    if (!fieldControl?.value) {
      this.disabled.set(true);
      this.childControl?.disable();
    }
  }

  isDependencyFieldsValueAvailable(): boolean {
    return this.filters().every(filter => {
      if (filter.value_type === filterInputTypesEnum.DYNAMIC) {
        const fieldControlName = filter.value.toString();
        const fieldControl =
          this.parentFormGroup?.controls[fieldControlName]?.value;
        return !!fieldControl;
      }
      return true;
    });
  }

  ngAfterViewInit(): void {
    if (this.childControl) {
      setTimeout(() => {
        this.writeValue(this.childControl.value);
      }, 50);
      this.childControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.writeValue(value, false);
        });
    }

    this.searchTerm$.pipe(debounceTime(600)).subscribe(term => {
      this.onDropdownSearch(term);
    });
  }

  onSearchTermChange(target: EventTarget | null) {
    const term = (target as HTMLInputElement).value;
    this.searchTerm$.next(term);
  }

  getLookupItems() {
    this.isLoading.set(true);

    const filters = [...this.filters()];

    const searchFilterIndex = filters.findIndex(
      filter => filter.key === this.searchFilterKey()
    );
    if (searchFilterIndex !== -1) {
      filters[searchFilterIndex] = {
        ...filters[searchFilterIndex],
        value: this.searchTerm(),
      };
    } else if (this.searchTerm()) {
      filters.push({ key: this.searchFilterKey(), value: this.searchTerm() });
    }

    this.lookupFieldService
      .getLookupOptions(
        this.apiUrl(),
        this.page(),
        filters,
        this.apiUrlParams,
        this.dynamicMultipleUrlParams()
      )
      .subscribe({
        next: (response: ApiResponse<LookupOptionItem>) =>
          this.handleLookupResponse(response),
      });
  }

  handleLookupResponse(response: ApiResponse<LookupOptionItem>) {
    if (this.page() === 1) {
      this.options.set([]);
      this.options.set(response['data']);
    } else {
      this.options()?.push(...response['data']);
    }

    if (response.meta.last_page) {
      this.totalPages.set(response.meta.last_page);
    }
    if (this.value()) {
      this.selectedOption.set(this.getSelectedOption(this.value() as number));
    }
    this.isLoading.set(false);
  }

  onScroll(event: Event) {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as unknown as {
        scrollTop: number;
        scrollHeight: number;
        clientHeight: number;
      };
    if (
      scrollTop + clientHeight >= scrollHeight - 200 &&
      !this.isLoading() &&
      this.page() < this.totalPages()
    ) {
      this.page.set(this.page() + 1);
      this.getLookupItems();
    }
  }

  getSelectedOption = (_value: number) => {
    const key = this.selectedKeyValue();
    return this.options()?.find(item => {
      // Try the configured key first, then fallback to 'value' for backward compatibility
      const itemValue = item[key] ?? item.value;
      return itemValue != null && +itemValue == _value;
    });
  };

  toggleDropdown() {
    this.childControl.markAsTouched();

    if (this.isDropdownOpen && !this.childControl?.value && this.required()) {
      this.childControl?.markAsDirty();
      this.childControl?.setErrors({ required: true });
    }

    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen || this.options()?.length) {
      this.options.set([]);
      this.page.set(1);
      this.searchTerm.set('');
      this.getLookupItems();
    }

    if (this.isDropdownOpen) {
      this.calculateDropdownPosition();
    } else {
      this.removeScrollListener();
    }
  }

  writeValue(value: string | number, updateFormControlValue?: boolean) {
    if (value && this.options()) {
      const key = this.selectedKeyValue();
      const match = this.options()?.find((item: LookupOptionItem) => {
        // Try the configured key first, then fallback to 'value' for backward compatibility
        const itemValue = item[key] ?? item.value;
        return itemValue != null && +itemValue == +value;
      });
      if (match) {
        this.selectOption(match, updateFormControlValue);
      }
    } else {
      this.selectedOption.set(<LookupOptionItem>{});
    }
  }

  selectOption(item: LookupOptionItem, updateFormControlValue?: boolean) {
    const key = this.selectedKeyValue();
    // Get value with fallback for backward compatibility
    const getItemValue = (option: LookupOptionItem) =>
      option[key] ?? option.value;
    const currentOption = this.selectedOption();
    const currentValue = currentOption ? getItemValue(currentOption) : null;
    const newValue = getItemValue(item);

    if (currentValue !== newValue) {
      this.selectedOption.set(item);
      if (updateFormControlValue) {
        this.childControl.patchValue(newValue);
      }

      if (this.childControl?.hasError('required') && newValue) {
        this.childControl?.setErrors(null);
      }

      this.changeOption.emit(item);
      this.notEmpty = true;
    }
  }

  onDropdownSearch(term: string): void {
    this.currentSearchTerm.set(term);
    if (term.length >= 3) {
      this.isMoreThanThreeCharacters.set(true);
      this.page.set(1);
      this.searchTerm.set(term);
      this.getLookupItems();
    } else if (
      this.isMoreThanThreeCharacters() &&
      term.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      this.page.set(1);
      this.searchTerm.set('');
      this.getLookupItems();
    }
  }

  clickedOutside() {
    this.isDropdownOpen = false;
    this.removeScrollListener();
    if (
      this.childControl?.touched &&
      !this.childControl?.value &&
      this.required()
    ) {
      this.childControl?.markAsDirty();
      this.childControl?.setErrors({ required: true });
    }
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
